import { createTheme, ThemeOptions } from "@mui/material";
import type { } from "@mui/x-date-pickers/themeAugmentation";
import { BTTextFieldProps } from "@common-components/atoms/text-field/bt-text-field";
import { BTPaperProps } from "@common-components/atoms/bt-paper/bt-paper";

export const colors = {
  green: '#00c875'
}

/**
 * THE app-wide mobile breakpoint (in px).
 * 786 is the existing de-facto cutoff used across the app, kept here as the
 * single source of truth. Below this width the UI is treated as "mobile".
 * New code MUST consume this via `useIsMobile()` / `useIsDesktop()`
 * (src/common/hooks/use-is-mobile.ts) instead of hard-coding a number.
 */
export const MOBILE_BREAKPOINT = 786;

/**
 * The narrowest frame (in px) that shows two panes side by side — the agent
 * workspace's "Both" (chat + form). Below it a two-pane view renders as tabs.
 * 1280 because the app's left nav takes ~300px: at 1100 the chat pane would be
 * a ~350px box. This and MOBILE_BREAKPOINT are the ONLY layout breakpoints.
 */
export const WORKSPACE_SPLIT_MIN = 1280;

export const groupcolors = [
  "#f44336",
  "#e91e63",
  "#9c27b0",
  "#673ab7",
  "#3f51b5",
  "#2196f3",
  "#03a9f4",
  "#00bcd4",
  "#009688",
  "#00C875",
  "#8bc34a",
  "#cddc39",
  "#ffeb3b",
  "#ffc107",
  "#ff9800",
  "#ff5722",
  "#795548",
  "#607d8b"
];

const baseTheme: ThemeOptions = {
  direction: "ltr",
  palette: {
    primary: {
      main: "#53bdeb",
    },
    secondary: {
      main: "#2C2E2F",
    },
  },
  typography: {
    fontFamily: "Montserrat, sans-serif",
  },
  components: {
    // MUI's FocusTrap (`contain`, a document `focusin` listener) and Radix's FocusScope
    // (`handleFocusOut`, a document `focusout` listener) each force focus back into their
    // OWN container, and the two libraries keep separate stacks — neither pauses for the
    // other. So a MUI popover opened inside a Radix <Dialog> (e.g. the country <Select> in
    // BtMUIValidatedPhone inside ConnectGatewayDialog) recurses focusin -> focusout ->
    // focusin synchronously until "Maximum call stack size exceeded" takes the page down.
    // A popover is not a modal and does not need focus containment, so we stand MUI down
    // and let the surrounding trap (Radix's, or none) own focus. Menu/Select/Autocomplete
    // all render through Popover, so this covers the family.
    MuiPopover: {
      defaultProps: {
        disableEnforceFocus: true,
      },
    },
    MuiMenu: {
      defaultProps: {
        disableEnforceFocus: true,
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: "var(--color-text)",
        },
        body2: {
          color: "var(--color-text)",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          ...BTPaperProps,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: "var(--color-primary)",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            opacity: 0.75,
            color: "var(--color-text-muted)",
          },
        },
        containedPrimary: {
          color: "var(--color-primary-contrast)",
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: "var(--color-primary)",
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          color: "var(--dialog-text)",
        }
      }
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          ".MuiTypography-root.ListItemText-secondary": {
            color: "var(--color-text-muted)",
          },
          "& .ListItemText-secondary": {
            color: "var(--color-text-muted)",
          },
        },
      },
    },
    // MuiPickerStaticWrapper: {
    //   styleOverrides: {
    //     content: {
    //       backgroundColor: 'var(--tasks-board-bg)'
    //     },
    //   },
    // },
    MuiTextField: {
      styleOverrides: {
        root: ({
          ...BTTextFieldProps,
        }),
      },
    },
    // MuiCalendarPicker: {
    //   styleOverrides: {
    //     root: ({
    //       color: "var(--color-primary)",
    //       ".MuiButtonBase-root": {
    //         color: "var(--color-primary)",
    //         "&.Mui-disabled": {
    //           opacity: 0.5,
    //           color: "var(--color-primary)",
    //         },
    //       },
    //       ".MuiTypography-root": {
    //         color: "var(--color-primary)",
    //       },
    //       ".MuiYearPicker-root": {
    //         ".Mui-disabled": {
    //           color: "210 40% 96.1%",
    //         },
    //       },
    //       ".MuiPickersDay-root": {
    //         color: "var(--color-primary)",
    //         backgroundColor: "var(--tasks-board-bg)",

    //         "&.Mui-disabled": {
    //           opacity: 0.5,
    //           color: "var(--color-primary)",
    //         },
    //       },
    //     }),
    //   },
    // },
    // MuiClockPicker: {
    //   styleOverrides: {
    //     root: ({
    //       '[role="option"]': {
    //         color: "var(--color-primary)",
    //       },
    //     }),
    //   },
    // },
  },
};

export const lightTheme = createTheme({
  ...baseTheme,
  palette: {
    ...baseTheme.palette,
    mode: "light",
  },
});
export const darkTheme = createTheme({
  ...baseTheme,
  palette: {
    ...baseTheme.palette,
    mode: "dark",
  },
});

export const theme = lightTheme;



export const blueticksDefultTheme: any = {

  boards: {
    tasksHeader: {
      background: 'var(--tasks-board-bg)',
    },
    tasksPanel: {
      background: 'var(--tasks-group-bg)',
      tasks: {
        border: 'transparent',
      },
      tasksHeader: {
        backgorund: undefined,
      }
    },
  }

};

export const blueticksLeanTheme: any = {
  boards: {
    tasksHeader: {
      background: 'var(--tasks-board-bg)',
    },
    tasksPanel: {
      background: 'var(--tasks-group-bg)',
      tasks: {
        border: 'transparent'
      },
      tasksHeader: {
        backgorund: undefined
      }
    }
  }
};

